import { createContext, useContext, useState, useEffect } from "react";
import * as FR from "./data/content";
import * as EN from "./data/content_en";

const LangCtx = createContext({ lang: "fr", setLang: () => {}, t: (k) => k });

export const UI = {
  fr: {
    "nav.accueil": "Accueil",
    "nav.services": "Nos services",
    "nav.creation": "Création d'entreprise",
    "nav.diaspora": "Diaspora",
    "nav.etrangers": "Étrangers",
    "nav.apropos": "À propos",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.suivi": "Suivi dossier",
    "nav.concours": "Concours",
    "cta.start": "Démarrer ma démarche",
    "cta.whatsapp": "Nous contacter sur WhatsApp",
    "cta.whatsappShort": "WhatsApp",
    "float.help": "Assistance en direct",
    "footer.tagline": "Vos démarches au Togo, en toute simplicité. Accompagnement administratif, entrepreneurial et mobilité — pour la diaspora, les résidents, les étrangers et les entreprises.",
    "footer.services": "Services",
    "footer.platform": "Plateforme",
    "footer.contact": "Contact",
    "footer.hours": "Lun – Sam · 8h – 18h (GMT)",
    "footer.rights": "© 2026 DOC TOGO — Tous droits réservés.",
    "footer.slogan": "Un besoin. Une démarche. Un accompagnement.",
    "footer.admin": "Espace équipe",
    "footer.confidentialite": "Politique de confidentialité",
    "footer.conditions": "Conditions d'utilisation",
    "footer.ambassades": "Ambassades & consulats",
    "footer.demande": "Demander une démarche",
    "footer.suivi": "Suivi de dossier",
    "footer.apropos": "À propos",
    "footer.blog": "Blog & guides",
    "footer.callsWhats": "(appels & WhatsApp)",
    "ctaband.title": "Prêt à démarrer votre démarche ?",
    "ctaband.sub": "Décrivez votre besoin en 2 minutes. Un conseiller DOC TOGO vous répond sous 24h avec les informations et conditions.",
    "process.eyebrow": "Comment ça marche",
    "process.title1": "Cinq étapes.",
    "process.title2": "Zéro complication.",
    "process.sub": "Un parcours pensé pour être suivi depuis Lomé, Paris, New York ou n'importe où dans le monde.",
    "process.cta": "Démarrer maintenant",
    "hero.cta": "Démarrer ma démarche",
  },
  en: {
    "nav.accueil": "Home",
    "nav.services": "Our services",
    "nav.creation": "Company formation",
    "nav.diaspora": "Diaspora",
    "nav.etrangers": "Foreign nationals",
    "nav.apropos": "About us",
    "nav.blog": "Blog",
    "nav.faq": "FAQ",
    "nav.suivi": "Track my case",
    "nav.concours": "Contest",
    "cta.start": "Start my request",
    "cta.whatsapp": "Contact us on WhatsApp",
    "cta.whatsappShort": "WhatsApp",
    "float.help": "Live assistance",
    "footer.tagline": "Your paperwork in Togo, made simple. Administrative, business and mobility support — for the diaspora, residents, foreign nationals and companies.",
    "footer.services": "Services",
    "footer.platform": "Platform",
    "footer.contact": "Contact",
    "footer.hours": "Mon – Sat · 8am – 6pm (GMT)",
    "footer.rights": "© 2026 DOC TOGO — All rights reserved.",
    "footer.slogan": "One need. One process. One partner.",
    "footer.admin": "Team area",
    "footer.confidentialite": "Privacy policy",
    "footer.conditions": "Terms of use",
    "footer.ambassades": "Embassies & consulates",
    "footer.demande": "Start a request",
    "footer.suivi": "Track my case",
    "footer.apropos": "About us",
    "footer.blog": "Blog & guides",
    "footer.callsWhats": "(calls & WhatsApp)",
    "ctaband.title": "Ready to start your request?",
    "ctaband.sub": "Describe your need in 2 minutes. A DOC TOGO advisor replies within 24h with information and terms.",
    "process.eyebrow": "How it works",
    "process.title1": "Five steps.",
    "process.title2": "Zero hassle.",
    "process.sub": "A journey designed to be followed from Lomé, Paris, New York or anywhere in the world.",
    "process.cta": "Start now",
    "hero.cta": "Start my request",
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("doctogo_lang") || "fr");
  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("doctogo_lang", lang);
  }, [lang]);
  const setLang = (l) => setLangState(l);
  const t = (key) => UI[lang][key] ?? UI.fr[key] ?? key;
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);

const mergeLists = (frList, enList, lang) =>
  lang === "en" ? frList.map((item, i) => ({ ...item, ...(enList[i] || {}) })) : frList;

export const useContent = () => {
  const { lang } = useLang();
  return {
    SERVICES: mergeLists(FR.SERVICES, EN.SERVICES_EN, lang),
    STEPS: mergeLists(FR.STEPS, EN.STEPS_EN, lang),
    VALUES: mergeLists(FR.VALUES, EN.VALUES_EN, lang),
    TESTIMONIALS: mergeLists(FR.TESTIMONIALS, EN.TESTIMONIALS_EN, lang),
    FAQ_ITEMS: mergeLists(FR.FAQ_ITEMS, EN.FAQ_ITEMS_EN, lang),
    WHY_POINTS: mergeLists(FR.WHY_POINTS, EN.WHY_POINTS_EN, lang),
    MARQUEE_ITEMS: lang === "en" ? EN.MARQUEE_EN : FR.MARQUEE_ITEMS,
    PROFILS: FR.PROFILS.map((value, i) => ({ value, label: lang === "en" ? EN.PROFILS_EN[i] : value })),
    FORM_CATEGORIES: Object.keys(FR.FORM_SERVICES).map((value, i) => ({
      value,
      label: lang === "en" ? EN.FORM_CATEGORIES_EN[i] : value,
    })),
    FORM_SERVICES_FOR: (categorie) =>
      (FR.FORM_SERVICES[categorie] || []).map((value, i) => ({
        value,
        label: lang === "en" ? (EN.FORM_SERVICES_EN[categorie] || [])[i] || value : value,
      })),
  };
};
